/**
 * EdgeStatPro Dashboard — API client
 * Static mode: loads pre-exported JSON from /data/ directory (Cloudflare Pages)
 * Live mode: polls live session data from tunnel when available
 */

export interface FilterState {
  dow?: number;
  gapDir?: 'up' | 'down' | 'flat';
  openPos?: 'inside' | 'above' | 'below';
  gapMin?: number;
  gapMax?: number;
  tf?: 'm5' | 'm15' | 'm30' | 'h1';
  stopPct?: 100 | 75 | 50 | 25;
}
// Falls back to same-origin for local dev
const LIVE_API_BASE = (window as any).__LIVE_API_BASE || '';

export interface SessionContextData {
  instrument: string;
  sessionBar: number;
  sessionRangePct: number;
  asr: number;
  sessionHigh: number;
  sessionLow: number;
  bar18Alert: boolean;
}

export async function fetchSessionContext(inst: string): Promise<SessionContextData | null> {
  try {
    if (LIVE_API_BASE) {
      const res = await fetch(`${LIVE_API_BASE}/api/session-context/${inst}`, { signal: AbortSignal.timeout(5000) });
      if (res.ok) return await res.json();
    }
  } catch {
    // fall through
  }
  return null;
}

export interface Instrument {
  key: string;
  code: string;
  name: string;
  pip: number;
}

export interface SessionSnapshot {
  instrument: string;
  current_price: number;
  session_open: number;
  session_high: number;
  session_low: number;
  prev_close: number;
  gap_points: number;
  gap_pct: number;
  orb_high: number;
  orb_low: number;
  prev_high: number;
  prev_low: number;
  yesterday_high: number;
  yesterday_low: number;
  updated_at: string;
}

export interface StatItem {
  type: string;
  filter: string;
  data: Record<string, unknown>;
  sampleSize: number;
}

export interface GapData {
  gapFillRate: number;
  bullishPct: number;
}

export interface ORBData {
  timeframe: string;
  breakUpPct: number;
  breakDownPct: number;
  avgExtensionUp: number;
  avgExtensionDown: number;
  hitRate1R: number;
  hitRate2R: number;
  hitRate3R: number;
  hitRate4R: number;
  hitRate5R: number;
}

export interface SessionTypeData {
  sessionType: string;
  probability: number;
  avgRange: number;
}

export interface TimingData {
  hour: number;
  highPct: number;
  lowPct: number;
}

export interface MultiDayData {
  pattern: string;
  directionalPct: number;
  sampleSize: number;
}

export interface SimilarDayData {
  gapPctAdr: number;
  gapDirection: 'up' | 'down' | null;
  similarGapsClosedPct: number;
  avgGapCloseCandle: number;
  hitPrevSessHighPct: number;
  hitPrevSessLowPct: number;
  hitPrevDailyHighPct: number;
  hitPrevDailyLowPct: number;
  hitPrevWeekHighPct: number;
  hitPrevWeekLowPct: number;
  similarCount: number;
  openPosition: 'inside' | 'above' | 'below' | null;
}

export interface LevelProbability {
  hitRate: number;
  hitCount: number;
  sampleSize: number;
  avgCandleHit: number;
}

export interface LevelProbData {
  [key: string]: LevelProbability;
}

export async function fetchInstruments(): Promise<Instrument[]> {
  const res = await fetch(`/data/instruments.json`);
  const data = await res.json();
  return data.instruments;
}

export async function fetchSessionToday(inst: string): Promise<SessionSnapshot | null> {
  try {
    if (LIVE_API_BASE) {
      const res = await fetch(`${LIVE_API_BASE}/api/session/${inst}/today`, { signal: AbortSignal.timeout(5000) });
      if (res.ok) {
        const data = await res.json();
        return data.snapshot || null;
      }
    }
    const res = await fetch(`/data/${inst}/session.json`);
    const data = await res.json();
    return data.snapshot || null;
  } catch {
    try {
      const res = await fetch(`/data/${inst}/session.json`);
      const data = await res.json();
      return data.snapshot || null;
    } catch {
      return null;
    }
  }
}

export async function fetchGapStats(inst: string): Promise<StatItem[]> {
  const res = await fetch(`/data/${inst}/gap.json`);
  const data = await res.json() as { stats?: StatItem[] };
  return data.stats || [];
}

export async function fetchORBStats(inst: string): Promise<StatItem[]> {
  const res = await fetch(`/data/${inst}/orb.json`);
  const data = await res.json() as { stats?: StatItem[] };
  return data.stats || [];
}

export async function fetchSessionTypeStats(inst: string): Promise<StatItem[]> {
  const res = await fetch(`/data/${inst}/session_type.json`);
  const data = await res.json() as { stats?: StatItem[] };
  return data.stats || [];
}

export async function fetchTimingStats(inst: string): Promise<StatItem[]> {
  const res = await fetch(`/data/${inst}/timing.json`);
  const data = await res.json() as { stats?: StatItem[] };
  return data.stats || [];
}

export async function fetchMultiDayStats(inst: string): Promise<StatItem[]> {
  const res = await fetch(`/data/${inst}/multi_day.json`);
  const data = await res.json() as { stats?: StatItem[] };
  return data.stats || [];
}

export async function fetchSimilarDays(inst: string): Promise<SimilarDayData> {
  try {
    if (LIVE_API_BASE) {
      const res = await fetch(`${LIVE_API_BASE}/api/similar-days/${inst}`, { signal: AbortSignal.timeout(5000) });
      if (res.ok) return await res.json();
    }
  } catch {
    // fall through to empty
  }
  return {
    gapPctAdr: 0, gapDirection: null, similarGapsClosedPct: 0, avgGapCloseCandle: 0,
    hitPrevSessHighPct: 0, hitPrevSessLowPct: 0, hitPrevDailyHighPct: 0, hitPrevDailyLowPct: 0,
    hitPrevWeekHighPct: 0, hitPrevWeekLowPct: 0, similarCount: 0, openPosition: null,
  };
}

export async function fetchLevelProbabilities(inst: string): Promise<LevelProbData> {
  try {
    if (LIVE_API_BASE) {
      const res = await fetch(`${LIVE_API_BASE}/api/level-probabilities/${inst}`, { signal: AbortSignal.timeout(5000) });
      if (res.ok) return await res.json();
    }
  } catch {
    // fall through to empty
  }
  return {};
}

export async function fetchAdr(inst: string): Promise<{ adrPct: number; adrPoints: number }> {
  try {
    if (LIVE_API_BASE) {
      const res = await fetch(`${LIVE_API_BASE}/api/adr/${inst}`, { signal: AbortSignal.timeout(5000) });
      if (res.ok) return await res.json();
    }
  } catch { /* fall through */ }
  return { adrPct: 0, adrPoints: 0 };
}

export async function refreshSession(inst: string): Promise<unknown> {
  try {
    if (LIVE_API_BASE) {
      const res = await fetch(`${LIVE_API_BASE}/api/session/${inst}/refresh`, {
        method: 'POST',
        signal: AbortSignal.timeout(5000)
      });
      const data = await res.json() as { tick?: unknown };
      return data.tick || null;
    }
    return null;
  } catch {
    return null;
  }
}

export async function fetchFilteredStats(inst: string, filters: FilterState): Promise<{
  gapStats: StatItem[];
  orbStats: StatItem[];
  sessionTypeStats: StatItem[];
  timingStats: StatItem[];
  multiDayStats: StatItem[];
  sampleSize: number;
}> {
  const params = new URLSearchParams();
  if (filters.dow !== undefined) params.set('dow', String(filters.dow));
  if (filters.gapDir) params.set('gap_dir', filters.gapDir);
  if (filters.openPos) params.set('open_pos', filters.openPos);
  if (filters.gapMin !== undefined) params.set('gap_min', String(filters.gapMin));
  if (filters.gapMax !== undefined) params.set('gap_max', String(filters.gapMax));
  if (filters.tf) params.set('tf', filters.tf);
  if (filters.stopPct) params.set('stop_pct', String(filters.stopPct));

  if (LIVE_API_BASE) {
    try {
      const res = await fetch(`${LIVE_API_BASE}/api/filtered-stats/${inst}?${params}`, { signal: AbortSignal.timeout(15000) });
      if (res.ok) {
        const data = await res.json();
        return {
          gapStats: (data.gapStats || []).map((s: any) => ({ type: 'gap', filter: s.filter, data: s.data, sampleSize: s.sampleSize })),
          orbStats: (data.orbStats || []).map((s: any) => ({
            type: 'orb',
            filter: `tf:${s.timeframe}`,
            data: { ...s.data, timeframe: s.timeframe },
            sampleSize: s.sampleSize,
          })),
          sessionTypeStats: (data.sessionTypeStats || []).map((s: any) => ({ type: 'session_type', filter: s.filter, data: s.data, sampleSize: s.sampleSize })),
          timingStats: (data.timingStats || []).map((s: any) => ({ type: 'timing', filter: `hour:${s.hour}`, data: s.data, sampleSize: s.sampleSize })),
          multiDayStats: (data.multiDayStats || []).map((s: any) => ({ type: 'multi_day', filter: s.filter, data: s.data, sampleSize: s.sampleSize })),
          sampleSize: data.sampleSize || 0,
        };
      }
    } catch {
      // fall through to empty
    }
  }

  return { gapStats: [], orbStats: [], sessionTypeStats: [], timingStats: [], multiDayStats: [], sampleSize: 0 };
}

// ── Fundamentals API ──────────────────────────────────────────────

export interface CurrencyData {
  code: string;
  name: string;
  interestRate: number | null;
  interestRateChange: number | null;
  inflation: number | null;
  inflationChange: number | null;
  gdp: number | null;
  gdpChange: number | null;
  unemployment: number | null;
  unemploymentChange: number | null;
  cotLong: number | null;
  cotShort: number | null;
  updatedAt: string;
}

export async function fetchFundamentals(): Promise<{ fundamentals: CurrencyData[]; source: 'cache' | 'fresh' }> {
  try {
    if (LIVE_API_BASE) {
      const res = await fetch(`${LIVE_API_BASE}/api/fundamentals`, { signal: AbortSignal.timeout(15000) });
      if (res.ok) return await res.json();
    }
  } catch { /* fall through */ }
  return { fundamentals: [], source: 'cache' };
}

export async function refreshFundamentals(): Promise<CurrencyData[]> {
  try {
    if (LIVE_API_BASE) {
      const res = await fetch(`${LIVE_API_BASE}/api/fundamentals/refresh`, {
        method: 'POST',
        signal: AbortSignal.timeout(30000),
      });
      if (res.ok) {
        const data = await res.json() as { fundamentals: CurrencyData[] };
        return data.fundamentals;
      }
    }
  } catch { /* fall through */ }
  return [];
}

export function formatPrice(price: number, pip = 0.01): string {
  return price.toFixed(pip <= 0.01 ? 2 : pip <= 0.1 ? 1 : 0);
}

export function formatPct(pct: number): string {
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
}
